/**
 * A VARIABLE WHERE THE RUNG DOES NOT SUPPORT ONE — the one check that belongs to no block.
 *
 * It is here rather than in any block because it is not about what a block DOES with a variable.
 * It is about whether a variable may be in that block at all, which is the same question for all
 * seventeen and which none of them can answer from its own data.
 *
 * ## Why it is checked on stored content rather than gated on the control
 *
 * Because the control is not the only way in, and is not even the easy one. Pasting a chip
 * reconstitutes it through the extension's `parseHTML`; typing `{dip}` into a formula field makes
 * one with no clipboard at all, since there a chip is a regex over a stored string and a gated
 * `{` is a literal brace by design. Duplicating a block, pulling a task and lowering the rung
 * after authoring each deliver one with no authoring action whatsoever.
 *
 * Nor is any of that grandfathering. Grandfathering protects work authored while the capability
 * was available; a bypass at the CURRENT rung is a workaround, and the two are byte-identical in
 * storage. The owner put it plainly: pasting a Numerical block's chip into an MCQ "is a workaround
 * and it creates variable mcq questions that can't be answered". So the rule is about what is
 * SUPPORTED, never about how it got there — which is also the only rule that can be implemented.
 *
 * ## Why its own module
 *
 * TWO callers need it and a cycle separates them. The board reaches it through
 * `block-issue-dispatch`, which imports all seventeen block files; the editor reaches it through
 * `IssuesPanel`, which every one of those block files imports. Importing the dispatcher from the
 * panel would close that loop. A leaf module both can import closes nothing.
 */
import type { BlockIssue } from '@/components/task/block-issues';
import type { Block, TaskVariable } from '@/types/experience';

import { isVarUsed } from '@/components/task/variable-utils';
import { variableAllowedInBlock } from '@/lib/variable-rungs';


export const VARIABLE_NOT_SUPPORTED_KEY = 'variable-not-supported-here';

/**
 * @param rung     the Variables capability's level. `undefined` means the caller cannot know it,
 *                 and the check stays silent rather than guessing: guessing high hides a real
 *                 defect, and guessing low raises an error against content a teacher is entitled
 *                 to. The invariants pin that both real callers do pass it.
 * @param variables the set in force where this block sits — task plus experience.
 */
export function variableRungIssues(
  block: Pick<Block, 'type' | 'data'>,
  rung: number | undefined,
  variables: TaskVariable[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): BlockIssue[] {
  if (rung === undefined) return [];
  if (variableAllowedInBlock(rung, block.type)) return [];

  /*
   * By NAME against the variables in force, rather than by looking for a brace. A bare `{` is
   * ordinary text a teacher may legitimately have typed, and `isVarUsed` already knows both
   * spellings a reference takes — `{name}` in a formula and `data-variable-chip` in prose — and
   * already skips the inert banked branch draft, which can hold a name it no longer uses.
   */
  const used = variables
    .map(v => v.name)
    .filter(name => name && isVarUsed(name, [block as Block]));
  if (used.length === 0) return [];

  return [{
    key: VARIABLE_NOT_SUPPORTED_KEY,
    severity: 'error',
    kind: 'defect',
    category: t('blocks.variables.issueCategory'),
    /*
     * An ERROR, because the artifact is not merely advanced, it is unanswerable: a question whose
     * text varies while its correct answer does not is wrong for every roll but one, and the
     * mechanism that would vary the answer lives on the top rung. So it holds publication until
     * the teacher removes the chip or raises the rung.
     */
    // Braced, because a variable's name is never bare text in this app.
    detail: t('blocks.variables.notSupportedInBlock', {
      names: used.map(n => `{${n}}`).join(', '),
    }),
  }];
}
